import { useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, AlertCircle, ExternalLink, Download, Info } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useTrips, parseCSV, parseCSVRow } from "@/hooks/useTrips";
import { validateEarningsCSV } from "@/lib/csvValidation";
import { useSubscription } from "@/hooks/useSubscription";
import { cn } from "@/lib/utils";
import analytics from "@/lib/segment";

interface EarningsImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasTrips: boolean;
}

export const EarningsImportDialog = ({
  open,
  onOpenChange,
  hasTrips,
}: EarningsImportDialogProps) => {
  const { importTrips } = useTrips();
  const { vehicleLimit } = useSubscription();
  const [isDragging, setIsDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{
    totalVinsInCSV: number;
    vehiclesImported: number;
    skippedVehicles: number;
    tripsImported: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(async (files: FileList) => {
    const csvFiles = Array.from(files).filter(
      (f) => f.type === "text/csv" || f.name.endsWith(".csv")
    );
    if (csvFiles.length === 0) return;

    setValidationError(null);
    setProcessing(true);
    setImportResult(null);

    try {
      let allImportData: ReturnType<typeof parseCSVRow>[] = [];

      for (const file of csvFiles) {
        const content = await file.text();
        const { headers, rows } = parseCSV(content);

        const validation = validateEarningsCSV(headers);
        if (!validation.valid) {
          setValidationError(validation.message);
          setProcessing(false);
          return;
        }

        const fileData = rows
          .filter((row) => row.length > 1 && row[0])
          .map((row) => parseCSVRow(headers, row));

        allImportData = [...allImportData, ...fileData];
      }

      if (allImportData.length > 0) {
        const result = await importTrips.mutateAsync({
          importData: allImportData,
          vehicleLimit,
        });

        setImportResult({
          totalVinsInCSV: result.totalVinsInCSV,
          vehiclesImported: result.vehiclesImported,
          skippedVehicles: result.skippedVehicles,
          tripsImported: allImportData.length,
        });

        analytics.track("Earnings Imported", {
          trip_count: allImportData.length,
          source: "earnings_import_dialog",
        });
      }
    } catch (err: any) {
      setValidationError(err?.message || "Failed to import. Please try again.");
    } finally {
      setProcessing(false);
    }
  }, [importTrips, vehicleLimit]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files) handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) handleFiles(e.target.files);
    },
    [handleFiles]
  );

  const handleClose = () => {
    setImportResult(null);
    setValidationError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import Turo Earnings</DialogTitle>
          <DialogDescription>
            Upload one or more Turo earnings CSV files. Existing trips will be updated automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Instructions */}
          <Accordion type="single" collapsible defaultValue="instructions" className="w-full">
            <AccordionItem value="instructions" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-2 hover:no-underline">
                <div className="flex items-center gap-2 text-sm">
                  <Info className="h-4 w-4 text-primary" />
                  How to download your earnings from Turo
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-3 text-sm text-muted-foreground">
                  <p>
                    Go to{" "}
                    <a
                      href="https://turo.com/us/en/business/earnings?year=2026"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline inline-flex items-center gap-1"
                    >
                      Turo Earnings
                      <ExternalLink className="h-3 w-3" />
                    </a>{" "}
                    → <strong>Business → Earnings → Download CSV</strong> (click the button on the top right of the screen).
                  </p>
                  <p>Do this for each year you've been in business to import all your trip history.</p>
                  <div className="pt-2 border-t">
                    <a
                      href="/samples/earnings-sample.csv"
                      download="earnings-sample.csv"
                      className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
                    >
                      <Download className="h-4 w-4" />
                      Download sample CSV to try it out
                    </a>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Vehicle Limit Warning */}
          {importResult && importResult.skippedVehicles > 0 && (
            <Alert variant="destructive" className="bg-amber-500/10 border-amber-500/50">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <AlertTitle className="text-amber-600">Vehicle Limit Reached</AlertTitle>
              <AlertDescription>
                {importResult.totalVinsInCSV} vehicles found in CSV. {importResult.vehiclesImported} imported.{" "}
                <Link
                  to="/app/upgrade"
                  className="font-medium underline hover:no-underline"
                  onClick={() => analytics.track("Upgrade CTA Clicked", { source: "earnings_import_limit" })}
                >
                  Upgrade to unlock more vehicles.
                </Link>
              </AlertDescription>
            </Alert>
          )}

          {/* Success result */}
          {importResult && importResult.skippedVehicles === 0 && (
            <Alert className="border-green-500/50 bg-green-500/10">
              <AlertCircle className="h-4 w-4 text-green-500" />
              <AlertTitle className="text-green-700 dark:text-green-400">Import Successful</AlertTitle>
              <AlertDescription className="text-green-600 dark:text-green-300">
                Successfully imported {importResult.tripsImported} trip{importResult.tripsImported !== 1 ? "s" : ""}.
              </AlertDescription>
            </Alert>
          )}

          {/* Validation Error */}
          {validationError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Import Failed</AlertTitle>
              <AlertDescription>{validationError}</AlertDescription>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => setValidationError(null)}
              >
                Try Again
              </Button>
            </Alert>
          )}

          {/* Upload Zone */}
          {!validationError && !importResult && (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              className={cn(
                "border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer",
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-primary/50",
                processing && "opacity-50 pointer-events-none"
              )}
              onClick={() => fileInputRef.current?.click()}
            >
              <FileSpreadsheet className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-3">
                {processing
                  ? "Importing your earnings…"
                  : "Drag and drop your CSV files here, or click to browse"}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                multiple
                onChange={handleFileInput}
                className="hidden"
                disabled={processing}
              />
              <Button variant="outline" size="sm" disabled={processing} asChild>
                <span>
                  <Upload className="h-4 w-4 mr-2" />
                  Select Files
                </span>
              </Button>
            </div>
          )}

          {/* Done button after result */}
          {importResult && (
            <div className="flex justify-end">
              <Button onClick={handleClose}>Done</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
